import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { TabletPinSetupPanel } from "@/components/tabletPin/TabletPinSetupPanel";
import { useAuth } from "@/components/AuthProvider";
import { authLog } from "@/lib/auth-log";
import { HUB_GOOGLE_WORKSPACE_EMAIL_SUFFIX, isHubGoogleWorkspaceEmail } from "@/lib/hubSignInEmail";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { devAuthRedirectOrigin } from "@/lib/devPrimaryOrigin";

export function TabletPinPage() {
  const { session, loading } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signedInEmail = session?.user.email?.trim().toLowerCase() ?? "";
  const workspaceAccount = isHubGoogleWorkspaceEmail(signedInEmail);

  useEffect(() => {
    setError(null);
  }, [session?.user.id]);

  const handleGoogleSignIn = async (): Promise<void> => {
    if (!supabase) {
      setError("Supabase is not configured on this hub deployment.");
      return;
    }
    setBusy(true);
    setError(null);
    const redirectTo = `${devAuthRedirectOrigin()}/tablet-pin`;
    authLog("tabletPin:oauth:redirect", { provider: "google", redirectTo });
    const { error: authError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
        queryParams: {
          hd: "smartgridsolutions.com",
          access_type: "offline",
          prompt: "consent",
        },
      },
    });
    if (authError) {
      setError(authError.message);
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <p className="text-sm text-slate-600">Loading…</p>
      </div>
    );
  }

  if (session && workspaceAccount) {
    return <Navigate to="/settings#tablet-pin" replace />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Manufacturing tablet PIN</CardTitle>
        </CardHeader>
        <CardBody>
          {!session ? (
            <>
              <p className="text-sm text-slate-600">
                Sign in with your
                <span className="font-medium text-slate-900"> {HUB_GOOGLE_WORKSPACE_EMAIL_SUFFIX} </span>
                Google account, then choose a PIN for shared floor tablets.
              </p>
              {error ? (
                <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              ) : null}
              <Button
                className="mt-5 w-full min-h-12"
                onClick={() => void handleGoogleSignIn()}
                isLoading={busy}
                disabled={!isSupabaseConfigured}
              >
                Continue with Google
              </Button>
            </>
          ) : (
            <TabletPinSetupPanel signedInEmail={signedInEmail} workspaceAccount={workspaceAccount} />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
